<script lang="ts" setup>
import type { DotIndicatorReason } from '@/modules/Project/useDotIndicator'
import { exhaustiveGuard } from '@/shared/utils/typeAssertions'
import DotIndicator, { type DotIndicatorColor } from '@/uiKit/DotIndicator.vue'
import type { ToolTipProps } from '@/uiKit/ToolTip.vue'
import { computed, type ComputedRef } from 'vue'

const props = withDefaults(
  defineProps<{
    reason: DotIndicatorReason
    canRecompute?: boolean
    ariaLabel?: string
  }>(),
  {
    ariaLabel: 'Recalculate field',
    canRecompute: false,
  },
)

const emit = defineEmits<{
  (e: 'recompute'): void
}>()

// https://www.figma.com/file/1HfA941cU4A9RZxXHLmbpG/AGIDB---Design-System?node-id=5%3A2771&mode=dev

const tooltipTitle = computed(() => {
  switch (props.reason.cause) {
    case 'stale':
      return 'This field is stale'
    case 'error':
      return 'An error occurred'
    case 'locked':
      return 'This field is locked'
    case 'computing':
      return 'This field is being computed'
    case 'complete-with-error':
      return props.reason.detail
    case 'success':
      return ''
    case 'warning':
      return props.reason.detail || ''
    default:
      return exhaustiveGuard(props.reason, 'Unhandled cause')
  }
})

const tooltipBody = computed(() => {
  const baseStaleText = 'Recompute the field to get the latest value.'

  switch (props.reason.cause) {
    case 'error':
      return props.reason.detail || undefined
    case 'locked':
      return 'Locked fields are immutable and do not recompute.'
    case 'computing':
      return 'Please wait until the AI Tool generates a value.'
    case 'stale':
      switch (props.reason.detail) {
        case 'property_definition':
          return `The property definition has changed. ${baseStaleText}`
        case 'remote_input_data':
          return `An input has changed. ${baseStaleText}`
        default:
          return baseStaleText
      }
    default:
      return undefined
  }
})

const tooltipProps: ComputedRef<ToolTipProps> = computed(() => ({
  title: tooltipTitle.value,
  body: tooltipBody.value,
  placement: 'top',
  flipOptions: {
    fallbackPlacements: ['right', 'bottom'],
  },
}))

const recalculate = async () => {
  if (!props.canRecompute) {
    return
  }

  if (
    props.reason.cause === 'error' ||
    props.reason.cause === 'complete-with-error' ||
    props.reason.cause === 'stale' ||
    props.reason.cause === 'warning'
  ) {
    emit('recompute')
  }
}

const showRecalculateButton = computed(() => {
  if (!props.canRecompute) {
    return false
  }

  return (
    props.reason.cause === 'stale' ||
    props.reason.cause === 'complete-with-error' ||
    props.reason.cause === 'error'
  )
})

const color = computed<DotIndicatorColor>(() => {
  switch (props.reason.cause) {
    case 'error':
      return 'red'
    case 'complete-with-error':
    case 'warning':
    case 'stale':
      return 'yellow'
    case 'locked':
    case 'success':
      return 'green'
    case 'computing':
      return 'purple'
    default:
      return exhaustiveGuard(props.reason, 'Unhandled cause')
  }
})
</script>

<template>
  <DotIndicator
    :color="color"
    :clickable="showRecalculateButton"
    :aria-label="showRecalculateButton ? 'Recalculate field' : undefined"
    :tooltip="tooltipProps"
    @click.stop="recalculate"
  />
</template>
